import * as React from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { IconButton } from '@/components/ui/icon-button'
import { Input } from '@/components/ui/input'
import { useT } from '@/components/visitone/visitone-provider'
import { formatDateInput, parseDateInput, shiftDays, tenantToday } from '@/lib/format/tenant-date'

/*
 * DayNav: choose one tenant calendar day for a day-based list. Previous day,
 * the day typed in the tenant's pattern (as DateField), a calendar, next
 * day, then Today and Tomorrow. Every control but the typed date applies at
 * once and keeps focus, so a day can be stepped through; the typed date
 * applies on Enter or on leaving the field, only when its text changed, and
 * a date that isn't real shows the field error and emits nothing.
 *
 * `value` is the day shown (none, for example, for a link whose date isn't
 * real); `today` is the tenant's today, unknown until the caller has it.
 * Stepping needs one of them and Tomorrow needs `today`, so those controls
 * wait for it. `onToday` replaces the default Today, which emits `today`
 * unless it is already shown.
 */
function DayNav({
  id,
  value,
  today,
  onChange,
  onToday,
  zoneNote,
}: {
  id: string
  value?: string
  today?: string
  onChange: (isoDate: string) => void
  onToday?: () => void
  /** Says whose time zone the days are in, when it isn't the browser's. */
  zoneNote?: string
}) {
  const t = useT()
  const [text, setText] = React.useState(value ? formatDateInput(value) : '')
  const [error, setError] = React.useState<string>()
  const [open, setOpen] = React.useState(false)
  const trigger = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    setText(value ? formatDateInput(value) : '')
    setError(undefined)
  }, [value])

  const example = formatDateInput(today ?? tenantToday(null))
  const base = value ?? today
  const tomorrow = today ? shiftDays(today, 1) : undefined
  const choose = (day: string | undefined) => {
    if (day && day !== value) onChange(day)
  }

  const commit = () => {
    if (text === (value ? formatDateInput(value) : '')) return
    const parsed = parseDateInput(text)
    if (!parsed) {
      setError(t('core.dateField.invalid', { example }))
      return
    }
    setError(undefined)
    choose(parsed)
  }

  const close = React.useCallback(() => {
    setOpen(false)
    trigger.current?.focus()
  }, [])

  const labelId = `${id}-label`
  const helpId = `${id}-help`
  const errorId = `${id}-error`
  const zoneId = `${id}-zone`
  const stepClass = 'disabled:pointer-events-none disabled:opacity-50'

  return (
    <div role="group" aria-labelledby={labelId} className="flex min-w-0 flex-col gap-1.5">
      <label id={labelId} htmlFor={id} className="text-sm text-fg" style={{ fontWeight: 'var(--vo-label-font-weight)' }}>
        {t('core.dayNav.label')}
      </label>
      <p id={helpId} className="max-w-prose text-xs text-fg-muted">
        {t('core.dateField.example', { example })}
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="relative flex items-center">
          <IconButton
            label={t('core.dayNav.previous')}
            disabled={!base}
            className={stepClass}
            onClick={() => choose(base && shiftDays(base, -1))}
          >
            <ChevronLeft />
          </IconButton>
          <div className="flex items-center">
            <Input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              aria-describedby={[helpId, error ? errorId : '', zoneNote ? zoneId : ''].filter(Boolean).join(' ')}
              aria-invalid={error ? true : undefined}
              className="w-36 rounded-r-none tabular-nums"
              value={text}
              onChange={(event) => {
                setText(event.target.value)
                setError(undefined)
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                commit()
              }}
              onBlur={commit}
            />
            <button
              ref={trigger}
              type="button"
              aria-label={t('core.dayNav.choose')}
              title={t('core.dayNav.choose')}
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={() => setOpen((shown) => !shown)}
              className="-ml-px inline-flex h-10 w-11 items-center justify-center rounded-r-sm border border-line-strong bg-surface text-fg-muted hover:bg-surface-hover hover:text-fg"
            >
              <CalendarDays className="size-5" />
            </button>
            {open ? (
              <Calendar
                value={value}
                today={today}
                anchor={trigger}
                onSelect={(day) => {
                  close()
                  choose(day)
                }}
                onClose={close}
              />
            ) : null}
          </div>
          <IconButton
            label={t('core.dayNav.next')}
            disabled={!base}
            className={stepClass}
            onClick={() => choose(base && shiftDays(base, 1))}
          >
            <ChevronRight />
          </IconButton>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={!onToday && !today} onClick={onToday ?? (() => choose(today))}>
            {t('core.dayNav.today')}
          </Button>
          <Button variant="secondary" disabled={!tomorrow} onClick={() => choose(tomorrow)}>
            {t('core.dayNav.tomorrow')}
          </Button>
        </div>
      </div>
      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
      {zoneNote ? (
        <p id={zoneId} className="max-w-prose text-xs text-fg-muted">
          {zoneNote}
        </p>
      ) : null}
    </div>
  )
}

export { DayNav }
